import { motion } from 'framer-motion';
import { Trees, Mountain, Feather, Compass, Sparkles, Flame } from 'lucide-react';

interface ThemeOption {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  preview: {
    bg: string;
    accent: string;
    glow: string;
  };
}

const themes: ThemeOption[] = [
  {
    id: 'theme-rust-sage',
    label: 'Rust & Sage',
    description: 'Warm earth tones, grounded and sophisticated',
    icon: <Flame className="w-5 h-5" />,
    preview: { bg: 'bg-[hsl(30,20%,96%)]', accent: 'bg-primary', glow: 'shadow-[0_0_20px_-5px_hsl(12,60%,45%/0.3)]' },
  },
  {
    id: 'theme-forest-trail',
    label: 'Forest Trail',
    description: 'Deep greens and mossy tones, like walking through an ancient wood',
    icon: <Trees className="w-5 h-5" />,
    preview: { bg: 'bg-[hsl(140,15%,95%)]', accent: 'bg-[hsl(145,35%,38%)]', glow: 'shadow-[0_0_20px_-5px_hsl(145,35%,38%/0.3)]' },
  },
  {
    id: 'theme-mountain-summit',
    label: 'Mountain Summit',
    description: 'Cool slate and alpine blue, crisp air at the peak',
    icon: <Mountain className="w-5 h-5" />,
    preview: { bg: 'bg-[hsl(215,15%,95%)]', accent: 'bg-[hsl(220,45%,50%)]', glow: 'shadow-[0_0_20px_-5px_hsl(220,45%,50%/0.3)]' },
  },
  {
    id: 'theme-taking-flight',
    label: 'Taking Flight',
    description: 'Sky blues and golden sunrise, the thrill of liftoff',
    icon: <Feather className="w-5 h-5" />,
    preview: { bg: 'bg-[hsl(40,25%,96%)]', accent: 'bg-[hsl(35,80%,50%)]', glow: 'shadow-[0_0_20px_-5px_hsl(35,80%,50%/0.3)]' },
  },
  {
    id: 'theme-fantasy-quest',
    label: 'Fantasy Quest',
    description: 'Rich purples and enchanted golds, your story is legendary',
    icon: <Sparkles className="w-5 h-5" />,
    preview: { bg: 'bg-[hsl(270,15%,96%)]', accent: 'bg-[hsl(275,50%,45%)]', glow: 'shadow-[0_0_20px_-5px_hsl(275,50%,45%/0.3)]' },
  },
  {
    id: 'theme-open-road',
    label: 'Open Road',
    description: 'Dusty amber and horizon blue, adventure awaits',
    icon: <Compass className="w-5 h-5" />,
    preview: { bg: 'bg-[hsl(25,20%,95%)]', accent: 'bg-[hsl(200,50%,45%)]', glow: 'shadow-[0_0_20px_-5px_hsl(200,50%,45%/0.3)]' },
  },
];

interface ThemePickerProps {
  selections: string[];
  onToggle: (id: string) => void;
}

const ThemePicker = ({ selections, onToggle }: ThemePickerProps) => {
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Pick a visual theme for your results. This is purely aesthetic and won't affect your career paths.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {themes.map((theme, i) => {
          const selected = selections.includes(theme.id);
          return (
            <motion.button
              key={theme.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => {
                // Deselect other themes first by toggling off, then toggle this one
                const currentTheme = selections.find(s => s.startsWith('theme-'));
                if (currentTheme && currentTheme !== theme.id) {
                  onToggle(currentTheme);
                }
                onToggle(theme.id);
              }}
              className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                selected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                  : 'border-border bg-card hover:border-primary/40'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Color preview */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme.preview.accent} text-white ${theme.preview.glow} shrink-0`}>
                  {theme.icon}
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-semibold text-foreground">{theme.label}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{theme.description}</p>
                </div>
              </div>
              {/* Color dots */}
              <div className="flex gap-1.5 mt-3 ml-[52px]">
                <span className={`w-4 h-4 rounded-full ${theme.preview.bg} border border-border`} />
                <span className={`w-4 h-4 rounded-full ${theme.preview.accent}`} />
                <span className={`w-4 h-4 rounded-full ${theme.preview.bg} border border-border opacity-60`} />
              </div>
              {selected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
                >
                  <span className="text-xs">✓</span>
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemePicker;
